import { Metadata } from "@dropbox/dash-component-library";
import { List } from "@dropbox/dig-components/list";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { useStackPageAugustRevisionEnabled } from "@mirage/august-revision-hook";
import { useStackChatEnabled } from "@mirage/service-experimentation/useStackChatEnabled";
import { DEFAULT_SECTION_ID } from "@mirage/service-stacks/service/utils";
import { onKeyDownCommitFn } from "@mirage/shared/util/on-key-down";
import {
  activeStackActionAtom,
  activeStackAtom,
  activeStackLinkSectionsMapAtom,
  activeStackSectionIdAtom,
} from "@mirage/stacks/ActiveStack/atoms";
import i18n, { meticulousRedactedStringClass } from "@mirage/translations";
import classNames from "classnames";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { SectionsActionType } from "../types";
import { FancyInlineEdit } from "./FancyInlineEdit";
import { HeaderMenu } from "./Header";
import styles from "./Navigation.module.css";

import type { stacks } from "@dropbox/api-v2-client";

interface NavigationItemProps {
  hasWritePermissions: boolean;
  onDeleteSection: (sectionId: string) => void;
  onRenameSection: (newName: string, sectionId: string) => void;
  scrollToSection: (sectionId: string) => void;
  section: stacks.Section;
  // Optional function for tracking whether a section edit is in progress at the navigation level.
  setNavEditState?: (isEditing: boolean) => void;
  showCount?: boolean;
}

export const NavigationItem = ({
  hasWritePermissions,
  onDeleteSection,
  onRenameSection,
  scrollToSection,
  section,
  setNavEditState,
  showCount = true,
}: NavigationItemProps) => {
  const [isInteracting, setIsInteracting] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const linkSectionMap = useAtomValue(activeStackLinkSectionsMapAtom);
  const sectionId = section.id ?? DEFAULT_SECTION_ID;
  const items = linkSectionMap ? (linkSectionMap.get(sectionId) ?? []) : [];
  const isAugustRevision = useStackPageAugustRevisionEnabled();
  const isStackChatEnabled = useStackChatEnabled();
  const [isEditing, setIsEditing] = useState(false);
  const stack = useAtomValue(activeStackAtom);
  const [isHovering, setIsHovering] = useState(false);
  const [currentAction, setCurrentAction] = useAtom(activeStackActionAtom);
  const [currentSectionId, setCurrentSectionId] = useAtom(
    activeStackSectionIdAtom,
  );
  const [isHoveringOnMenu, setIsHoveringOnMenu] = useState(false);

  useEffect(() => {
    // If another section action occurs while an edit is in progress, cancel the current action
    if (currentAction !== SectionsActionType.EDIT_SECTION) {
      setIsEditing(false);
      setNavEditState?.(false);
    }
  }, [currentAction]);

  const handleInteractionStart = () => {
    setIsInteracting(true);
    setHasDragged(false);
  };

  const handleInteractionMove = () => {
    if (isInteracting) {
      setHasDragged(true);
    }
  };

  /**
   * Only scroll to the section if the user has not dragged the item.
   */
  const handleInteractionEnd = () => {
    if (!hasDragged && !isHoveringOnMenu) {
      scrollToSection(section.id ?? DEFAULT_SECTION_ID);
    }
    setIsInteracting(false);
    setHasDragged(false);
  };

  const handleSubmit = useCallback(
    (title: string) => {
      setIsEditing(false);
      setNavEditState?.(false);
      setCurrentAction(null);
      onRenameSection(title, sectionId);
    },
    [onRenameSection, sectionId, setCurrentAction],
  );

  if (section.id === DEFAULT_SECTION_ID) {
    return null;
  }

  const headerMenu = (
    <HeaderMenu
      isHoveringSection={isHovering}
      onDeleteSection={() => onDeleteSection(sectionId)}
      onEditSection={() => {
        setIsEditing(true);
        setNavEditState?.(true);
        setCurrentAction(SectionsActionType.EDIT_SECTION);
        setCurrentSectionId(sectionId);
      }}
      section={section}
      setIsHoveringOnMenu={setIsHoveringOnMenu}
      stack={stack}
    />
  );

  const fancyLineEdit = (
    <FancyInlineEdit
      title={section.title ?? ""}
      onSubmit={handleSubmit}
      onCancel={() => {
        setIsEditing(false);
        setNavEditState?.(false);
        setCurrentAction(null);
        setCurrentSectionId(null);
      }}
    />
  );

  if (isAugustRevision) {
    return (
      <List.Item
        tabIndex={0}
        className={classNames(styles.navigationItemAugust, {
          [styles.navigationItemChat]: isStackChatEnabled,
        })}
        // Don't use onClick as this will always fire even after drag
        onMouseDown={handleInteractionStart}
        onMouseMove={handleInteractionMove}
        onMouseUp={handleInteractionEnd}
        onTouchStart={handleInteractionStart}
        onTouchMove={handleInteractionMove}
        onTouchEnd={handleInteractionEnd}
        onKeyDown={onKeyDownCommitFn(() => {
          scrollToSection(section.id ?? DEFAULT_SECTION_ID);
        })}
        data-uxa-log={createUxaElementId("section_navigation_item", {
          featureLine: "stacks",
        })}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        spacing="small"
        onBlur={() => setIsHovering(false)}
      >
        <List.Content>
          {isEditing && section.id === currentSectionId ? (
            fancyLineEdit
          ) : (
            <Metadata size="medium" withDividers="bullet">
              <Metadata.Item>
                <Metadata.Label
                  tooltipControlProps={{
                    autoHide: true,
                  }}
                >
                  <Text
                    variant="label"
                    className={meticulousRedactedStringClass}
                  >
                    {section.title}
                  </Text>
                </Metadata.Label>
              </Metadata.Item>
              {showCount && (
                <Metadata.Item>
                  <Metadata.Label>
                    {i18n.t("num_items", {
                      count: items.length,
                    })}
                  </Metadata.Label>
                </Metadata.Item>
              )}
            </Metadata>
          )}
        </List.Content>
        {!isEditing && hasWritePermissions && headerMenu}
      </List.Item>
    );
  } else {
    // Cannot use a <button> tag because that is not draggable. We might be
    // able to make it work but safer to just use a <span> here.
    return (
      <span
        role="button"
        tabIndex={0}
        className={classNames(
          styles.navigationItem,
          styles.marchNavigationItem,
        )}
        // Don't use onClick as this will always fire even after drag
        onMouseDown={handleInteractionStart}
        onMouseMove={handleInteractionMove}
        onMouseUp={handleInteractionEnd}
        onTouchStart={handleInteractionStart}
        onTouchMove={handleInteractionMove}
        onTouchEnd={handleInteractionEnd}
        onKeyDown={onKeyDownCommitFn(() => {
          scrollToSection(section.id ?? DEFAULT_SECTION_ID);
        })}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        data-uxa-log={createUxaElementId("section_navigation_item", {
          featureLine: "stacks",
        })}
        onBlur={() => setIsHovering(false)}
      >
        <div className={styles.draggableSectionRow}>
          {isEditing && section.id === currentSectionId ? (
            fancyLineEdit
          ) : (
            <Text>
              <Truncate maxWidth={250}>{section.title}</Truncate>
            </Text>
          )}
          {!isEditing && hasWritePermissions && headerMenu}
        </div>
      </span>
    );
  }
};
